class QuizForm extends HTMLElement {
  constructor() {
    super();
    this.currentStep = 1;
  }

  connectedCallback() {
    this.bindEvents();
    this.showStep(1);
  }

  bindEvents() {
    this.addEventListener('click', (e) => {
      if (e.target.matches('[data-quiz-next]')) {
        e.preventDefault();
        this.nextStep();
      }

      if (e.target.matches('[data-quiz-exit]')) {
        e.preventDefault();
        window.location.href = '/';
      }
    });

    const form = this.querySelector('[data-quiz-form]');
    if (form) {
      form.addEventListener('submit', (e) => this.handleSubmit(e));
    }
  }

  showStep(stepNumber) {
    this.querySelectorAll('.quiz-step').forEach((step) => {
      step.removeAttribute('data-step-active');
    });

    const step = this.querySelector(`[data-step="${stepNumber}"]`);
    if (step) {
      step.setAttribute('data-step-active', '');
      this.currentStep = stepNumber;
    }

    if (stepNumber === 3 && !this.klaviyoListener) {
      this.setupKlaviyoListener();
    }

    if (stepNumber === 4) {
      this.startRedirect();
    }

    // Auto-scroll to important elements based on step
    this.scrollToElement(stepNumber);
  }

  scrollToElement(stepNumber) {
    setTimeout(() => {
      let targetElement = null;

      if (stepNumber === 1) {
        // Step 1: Scroll to buttons (call to action)
        targetElement = this.querySelector('[data-step="1"] .quiz-step__buttons');
      } else if (stepNumber === 2) {
        // Step 2: Scroll to buttons to see CTA immediately
        targetElement = this.querySelector('[data-step="2"] .quiz-step__buttons');
      } else if (stepNumber === 3) {
        // Step 3: Scroll to Klaviyo form
        targetElement = this.querySelector('[data-step="3"] .klaviyo-form-TQLEvJ') ||
                       this.querySelector('[data-step="3"] form');
      } else if (stepNumber === 4) {
        // Step 4: Scroll to top to see success message
        targetElement = this.querySelector('[data-step="4"]');
      }

      if (targetElement) {
        targetElement.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    }, 100);
  }

  nextStep() {
    this.showStep(this.currentStep + 1);
  }

  async handleSubmit(e) {
    e.preventDefault();

    const formData = new FormData(e.target);
    this.classList.add('quiz-form--loading');

    try {
      await fetch('/contact#contact_form', {
        method: 'POST',
        body: new URLSearchParams(formData),
      });

      if (window.klaviyo) {
        const email = formData.get('contact[email]');
        window.klaviyo.push(['identify', { $email: email }]);
      }

      // Push lead data to dataLayer for Facebook
      this.pushToDataLayer(formData);

      this.showStep(4);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      this.classList.remove('quiz-form--loading');
    }
  }

  pushToDataLayer(formData) {
    window.dataLayer = window.dataLayer || [];

    const leadData = {
      event: 'quiz_lead_submitted',
      user: {
        email: formData.get('contact[email]') || '',
        phone: formData.get('contact[phone]') || '',
        name: formData.get('contact[name]') || '',
      },
      eventCategory: 'Quiz',
      eventAction: 'Lead Submitted',
      eventLabel: 'Quiz Form',
    };

    // Collect all form fields
    for (let [key, value] of formData.entries()) {
      if (key.startsWith('contact[') && key.endsWith(']')) {
        const fieldName = key.replace('contact[', '').replace(']', '');
        if (!leadData.user[fieldName]) {
          leadData.user[fieldName] = value;
        }
      }
    }

    window.dataLayer.push(leadData);
  }

  captureKlaviyoFormData(form) {
    const formData = {
      email: '',
      phone: '',
      first_name: '',
      last_name: '',
    };

    // Extract email
    const emailInput =
      form.querySelector('input[type="email"]') || form.querySelector('input[name*="email"]') || form.querySelector('input[id*="email"]');
    if (emailInput) {
      formData.email = emailInput.value;
    }

    // Extract phone
    const phoneInput =
      form.querySelector('input[type="tel"]') || form.querySelector('input[name*="phone"]') || form.querySelector('input[id*="phone"]');
    if (phoneInput) {
      formData.phone = phoneInput.value;
    }

    // Extract first name
    const firstNameInput =
      form.querySelector('input[name*="first"]') ||
      form.querySelector('input[id*="first"]') ||
      form.querySelector('input[placeholder*="First"]') ||
      form.querySelector('input[placeholder*="first"]');
    if (firstNameInput) {
      formData.first_name = firstNameInput.value;
    }

    // Extract last name
    const lastNameInput =
      form.querySelector('input[name*="last"]') ||
      form.querySelector('input[id*="last"]') ||
      form.querySelector('input[placeholder*="Last"]') ||
      form.querySelector('input[placeholder*="last"]');
    if (lastNameInput) {
      formData.last_name = lastNameInput.value;
    }

    return formData;
  }

  pushKlaviyoToDataLayer(formData) {
    if (!formData) return;

    window.dataLayer = window.dataLayer || [];

    const leadData = {
      event: 'lead_form_submit',
      user: {
        email: formData.email || '',
        phone: formData.phone || '',
        first_name: formData.first_name || '',
        last_name: formData.last_name || '',
      },
      eventCategory: 'Quiz',
      eventAction: 'Lead Submitted',
      eventLabel: 'Quiz Form',
    };

    window.dataLayer.push(leadData);
  }

  startRedirect() {
    const redirectEl = this.querySelector('[data-redirect-seconds]');
    if (!redirectEl) return;

    let seconds = parseInt(redirectEl.dataset.redirectSeconds);
    if (seconds === 0) return;

    const messageEl = this.querySelector('.quiz-step__message');

    const updateMessage = () => {
      if (messageEl) {
        const originalText = messageEl.dataset.originalText || messageEl.textContent;
        if (!messageEl.dataset.originalText) {
          messageEl.dataset.originalText = originalText;
        }
        messageEl.textContent = originalText.replace(/\d+|XX/i, seconds);
      }
    };

    updateMessage();

    const interval = setInterval(() => {
      seconds--;
      updateMessage();

      if (seconds <= 0) {
        clearInterval(interval);
        window.location.href = '/';
      }
    }, 1000);
  }

  setupKlaviyoListener() {
    let formSubmitted = false;
    let successDetected = false;
    let klaviyoFormData = null;

    const checkForm = setInterval(() => {
      const klaviyoContainer = this.querySelector('.klaviyo-form-TQLEvJ');
      const form = klaviyoContainer?.querySelector('form');

      if (form && klaviyoContainer) {
        clearInterval(checkForm);

        const allButtons = Array.from(form.querySelectorAll('button[type="button"]'));
        const submitButton =
          allButtons.find(
            (btn) =>
              btn.textContent?.toLowerCase().includes('join') ||
              btn.textContent?.toLowerCase().includes('submit') ||
              btn.textContent?.toLowerCase().includes('waitlist')
          ) || allButtons[allButtons.length - 1];

        if (submitButton) {
          submitButton.addEventListener('click', () => {
            // Always capture latest form data on each submit attempt
            formSubmitted = true;
            this.submitTime = Date.now();
            klaviyoFormData = this.captureKlaviyoFormData(form);
          });
        }

        form.addEventListener('submit', () => {
          // Always capture latest form data on each submit attempt
          formSubmitted = true;
          this.submitTime = Date.now();
          klaviyoFormData = this.captureKlaviyoFormData(form);
        });

        const observer = new MutationObserver(() => {
          if (successDetected) return;

          const buttonStillExists = form.contains(submitButton);
          const formHidden = form.style.display === 'none' || form.classList.contains('hide') || form.hasAttribute('hidden');
          const errorMessage = klaviyoContainer.querySelector('.error-message') || klaviyoContainer.querySelector('[class*="error"]');

          const isSuccess = formHidden || (formSubmitted && !buttonStillExists);

          if (isSuccess) {
            successDetected = true;
            observer.disconnect();

            // Push Klaviyo lead to dataLayer with captured data
            this.pushKlaviyoToDataLayer(klaviyoFormData);

            setTimeout(() => {
              this.showStep(4);
            }, 500);
          } else if (errorMessage && formSubmitted) {
            // Don't reset formSubmitted - allow re-capture on next attempt
            formSubmitted = false;
          }
        });

        observer.observe(klaviyoContainer, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ['class', 'style', 'hidden'],
        });

        form.addEventListener(
          'submit',
          () => {
            this.submitTime = Date.now();
          },
          { once: false }
        );

        this.klaviyoListener = true;
      }
    }, 100);

    setTimeout(() => {
      clearInterval(checkForm);
    }, 10000);
  }
}

customElements.define('quiz-form', QuizForm);
